import { useState, useEffect, useRef } from 'react';
import db from '@/db';
import { useVisiblePolling, tryReopenDb } from '@/hooks/useVisiblePolling';
import { useAuth } from '@/context/AuthContext';
import { addItemToCommand, getNextAvailableCommandNumber, removeItemFromCommand } from '@/lib/commands';
import { formatCurrency } from '@/lib/formatters';
import { previewCommandReceipt } from '@/lib/printer';
import { printCommandReceipt } from '@/lib/printSmart';
import { Plus, Delete, CheckCircle, Wine, X, Sparkles, Bike, FileText, Printer } from 'lucide-react';
import { toast } from 'sonner';
import CoffeeComboButton from '@/components/CoffeeComboButton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

export default function GarcomPage() {
  const { operator } = useAuth();
  const [commandInput, setCommandInput] = useState('');
  const [activeCommand, setActiveCommand] = useState(null);
  const [beverages, setBeverages] = useState([]);
  const [suggestedNumber, setSuggestedNumber] = useState(null);
  const [preview, setPreview] = useState(null);
  const [printing, setPrinting] = useState(false);
  const refreshRef = useRef(null);

  const loadBeverages = async () => {
    const all = await db.beverages.toArray();
    setBeverages(all);
  };

  const loadSuggestion = async () => {
    const next = await getNextAvailableCommandNumber();
    setSuggestedNumber(next);
  };

  const refreshCommand = async (num) => {
    if (!num) return;
    try {
      const c = await db.commands.where('number').equals(Number(num)).first();
      setActiveCommand(c || null);
    } catch (err) {
      // v7.77 — refreshCommand roda num setInterval imperativo (nao usa
      // useVisiblePolling, pois seu ciclo de vida depende de qual comanda
      // esta ativa). Sem isso, um DatabaseClosedError (versionchange em
      // outro device) deixava a comanda ativa congelada ate F5 manual.
      const name = err?.name || '';
      const msg = err?.message || '';
      if (name === 'DatabaseClosedError' || /database.*closed/i.test(msg)) {
        await tryReopenDb();
      }
    }
  };

  useEffect(() => {
    loadBeverages();
  }, []);

  // v7.77 — Antes: setInterval cru sem recuperacao de DatabaseClosedError
  // (ticket #207625). useVisiblePolling ja resolve isso e roda 1x imediato,
  // igual ao comportamento anterior.
  useVisiblePolling(loadSuggestion, 5000);

  const pressKey = (k) => {
    if (k === 'clear') {
      setCommandInput('');
      setActiveCommand(null);
      return;
    }
    if (k === 'back') {
      setCommandInput((prev) => prev.slice(0, -1));
      return;
    }
    if (commandInput.length >= 4) return;
    setCommandInput((prev) => prev + k);
  };

  const openCommand = async () => {
    if (!commandInput) return toast.error('Digite o número');
    const c = await db.commands.where('number').equals(Number(commandInput)).first();
    if (!c) {
      toast.error(`Comanda #${commandInput} não existe. Peça para o buffet abrir primeiro.`);
      return;
    }
    if (c.status !== 'open') {
      toast.error(`Comanda #${commandInput} já foi fechada`);
      return;
    }
    setActiveCommand(c);
    toast.success(`Comanda #${c.number} aberta`);
    // Auto refresh a cada 3s enquanto ativa
    if (refreshRef.current) clearInterval(refreshRef.current);
    refreshRef.current = setInterval(() => refreshCommand(c.number), 8000);
  };

  useEffect(() => {
    return () => { if (refreshRef.current) clearInterval(refreshRef.current); };
  }, []);

  const addBeverage = async (bev) => {
    if (!activeCommand) return;
    try {
      const updated = await addItemToCommand(activeCommand.number, {
        name: bev.name,
        price: bev.price,
        quantity: 1,
        beverageId: bev.id,
      }, operator?.id);
      setActiveCommand(updated);
      toast.success(`+ ${bev.name}`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const removeItem = async (origIndex, it) => {
    if (!activeCommand) return;
    if (!window.confirm(`Remover ${it.quantity}× ${it.name} da comanda #${activeCommand.number}?\n\nEsta ação será registrada no Log de Auditoria.`)) return;
    try {
      const updated = await removeItemFromCommand(activeCommand.id, origIndex, operator?.id);
      setActiveCommand(updated);
      toast.success(`Removido: ${it.quantity}× ${it.name}`);
    } catch (err) {
      toast.error(err.message || 'Falha ao remover item');
    }
  };

  // Preview Extrato — abre dialog com texto formatado do cupom (não imprime)
  const handlePreview = () => {
    if (!activeCommand) return;
    try {
      const text = previewCommandReceipt(activeCommand);
      setPreview(text);
    } catch (err) {
      toast.error(err?.message || 'Falha ao gerar preview');
    }
  };

  // Imprimir Comanda — usa printSmart com fluxo web estável (iframe HTML)
  // que não trava a página em iOS Safari nem em browsers desktop.
  const handlePrint = async () => {
    if (!activeCommand || printing) return;
    setPrinting(true);
    try {
      const r = await printCommandReceipt(activeCommand);
      toast.success('Comanda enviada para impressão', {
        description: r.mode === 'windows'
          ? 'Confira o diálogo do sistema'
          : 'Via Jetway (ESC/POS)',
      });
    } catch (err) {
      toast.error(err?.message || 'Falha ao imprimir');
    } finally {
      setPrinting(false);
    }
  };

  const numpad = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['clear', '0', 'back'],
  ];

  return (
    <div data-testid="garcom-page" className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="max-w-xl mx-auto">
        <header className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-400">Garçom</p>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">
              {operator?.name || 'Operador'}
            </h2>
          </div>
          <a
            data-testid="garcom-delivery-link"
            href="/delivery"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 active:translate-y-[1px] transition-all"
            title="Abrir cardápio de Delivery em nova aba"
          >
            <Bike size={14} strokeWidth={2} /> Delivery
          </a>
        </header>

        {!activeCommand && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
                Número da Comanda
              </label>
              {suggestedNumber != null && (
                <button
                  data-testid="garcom-suggest-number"
                  onClick={() => setCommandInput(String(suggestedNumber))}
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-600 hover:text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1"
                  title="Usar o próximo número disponível"
                >
                  <Sparkles size={11} strokeWidth={2.25} />
                  Próximo livre: <span className="font-mono text-[13px]">#{suggestedNumber}</span>
                </button>
              )}
            </div>
            <div
              data-testid="garcom-command-display"
              className={`h-20 rounded-xl border-2 border-slate-200 bg-slate-50 flex items-center justify-center font-mono text-5xl font-black tracking-tighter ${commandInput ? 'text-slate-900' : 'text-slate-300'}`}
            >
              {commandInput || '—'}
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4">
              {numpad.flat().map((k) => {
                const isClear = k === 'clear';
                const isBack = k === 'back';
                const label = isClear ? <span className="text-xs font-bold">LIMPAR</span> : isBack ? <Delete size={22} strokeWidth={1.75} /> : k;
                return (
                  <button
                    key={k}
                    data-testid={`garcom-numpad-${k}`}
                    onClick={() => pressKey(k)}
                    className={`h-16 rounded-xl font-mono text-2xl font-bold border transition-all active:scale-[0.97] ${
                      isClear ? 'border-slate-200 bg-white text-slate-500 hover:bg-slate-100' :
                      isBack ? 'border-slate-200 bg-white text-slate-500 hover:bg-slate-100 flex items-center justify-center' :
                      'border-slate-200 bg-white text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            <button
              data-testid="garcom-open-cmd-btn"
              onClick={openCommand}
              disabled={!commandInput}
              className="w-full mt-4 h-14 rounded-xl bg-blue-600 text-white font-bold text-lg hover:bg-blue-700 active:translate-y-[1px] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Abrir Comanda
            </button>

            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 mb-2 text-center">ou</p>
              <CoffeeComboButton variant="full" onDone={(cmd) => { setCommandInput(String(cmd.number)); openCommand(); }} />
            </div>
          </div>
        )}

        {activeCommand && (
          <>
            <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Comanda Ativa</p>
                  <p data-testid="garcom-active-number" className="font-mono text-5xl font-black tracking-tighter text-slate-900">
                    #{activeCommand.number}
                  </p>
                </div>
                <button
                  data-testid="garcom-change-cmd-btn"
                  onClick={() => { setActiveCommand(null); setCommandInput(''); if (refreshRef.current) clearInterval(refreshRef.current); }}
                  className="px-4 h-10 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50"
                >
                  Trocar
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-[9px] uppercase tracking-wider font-bold text-slate-400">Buffet</p>
                  <p className="font-mono text-sm font-bold text-slate-900">{formatCurrency(activeCommand.buffetTotal || 0)}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-[9px] uppercase tracking-wider font-bold text-slate-400">Bebidas</p>
                  <p data-testid="garcom-items-total" className="font-mono text-sm font-bold text-slate-900">{formatCurrency(activeCommand.itemsTotal || 0)}</p>
                </div>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-2">
                  <p className="text-[9px] uppercase tracking-wider font-bold text-blue-600">Total</p>
                  <p data-testid="garcom-total" className="font-mono text-sm font-bold text-blue-700">{formatCurrency(activeCommand.total || 0)}</p>
                </div>
              </div>

              {/* Ações rápidas — Preview (verde) + Imprimir (amarelo) */}
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button
                  data-testid="garcom-preview-btn"
                  onClick={handlePreview}
                  className="h-11 rounded-xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 active:translate-y-[1px] transition-all flex items-center justify-center gap-2 shadow-sm"
                >
                  <FileText size={15} strokeWidth={2.25} /> Preview Extrato
                </button>
                <button
                  data-testid="garcom-print-btn"
                  onClick={handlePrint}
                  disabled={printing}
                  className="h-11 rounded-xl bg-amber-500 text-white font-bold text-sm hover:bg-amber-600 active:translate-y-[1px] transition-all flex items-center justify-center gap-2 shadow-sm disabled:opacity-60"
                >
                  <Printer size={15} strokeWidth={2.25} />
                  {printing ? 'Imprimindo...' : 'Imprimir Comanda'}
                </button>
              </div>
            </div>

            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 mb-2">
              Bebidas (toque para lançar)
            </p>
            <div className="grid grid-cols-2 gap-3">
              {beverages.length === 0 && (
                <div className="col-span-2 text-center py-12 text-slate-400">
                  <Wine size={36} className="mx-auto mb-2 opacity-40" />
                  <p>Nenhuma bebida cadastrada</p>
                </div>
              )}
              {beverages.map((b) => (
                <button
                  key={b.id}
                  data-testid={`garcom-bev-${b.id}`}
                  onClick={() => addBeverage(b)}
                  className="bg-white border border-slate-200 rounded-xl p-4 text-left hover:border-blue-400 hover:bg-blue-50/30 active:scale-[0.98] transition-all min-h-[90px]"
                >
                  <p className="font-bold text-slate-900 truncate">{b.name}</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{b.category}</p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="font-mono text-lg font-black text-slate-900 tracking-tighter">{formatCurrency(b.price)}</p>
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center">
                      <Plus size={16} strokeWidth={2.2} />
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {activeCommand.items?.length > 0 && (
              <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">
                    Itens lançados ({activeCommand.items.length})
                  </p>
                  <p className="text-[10px] text-slate-400">Toque no X para remover</p>
                </div>
                <div translate="no" className="space-y-1 text-sm max-h-64 overflow-y-auto pr-1">
                  {activeCommand.items.map((it, idx) => (
                    <div key={`${it.name}-${it.quantity}-${idx}`} data-testid={`garcom-item-row-${idx}`} translate="no" className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-100 last:border-0">
                      <span className="flex items-center gap-2 text-slate-700 min-w-0 flex-1">
                        <CheckCircle size={12} className="text-emerald-500 shrink-0" />
                        <span className="truncate">{it.quantity}x {it.name}</span>
                      </span>
                      <span className="font-mono font-bold text-slate-900 shrink-0">{formatCurrency(it.subtotal)}</span>
                      <button
                        data-testid={`garcom-remove-item-${idx}`}
                        onClick={() => removeItem(idx, it)}
                        className="shrink-0 w-7 h-7 rounded-md text-rose-500 hover:bg-rose-50 hover:text-rose-700 border border-transparent hover:border-rose-200 flex items-center justify-center transition-all"
                        title="Remover item (registra auditoria)"
                      >
                        <X size={14} strokeWidth={2.5} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Preview Dialog — texto formatado do extrato */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent data-testid="garcom-preview-dialog" className="max-w-md bg-white border border-slate-200 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">Preview Extrato</DialogTitle>
          </DialogHeader>
          <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-[11px] font-mono overflow-x-auto whitespace-pre leading-relaxed max-h-96">
            {preview}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
